/**
 * Reviewable start insight rulebook: thresholds + wording.
 * Coaches can tune these without changing comparison logic.
 */

import type { BetterDirection } from '../types';

export interface StartRuleDef {
  id: string;
  /** Metric key in start_stats / prestart-summary rows. */
  metric: string;
  label: string;
  unit: string;
  /** Absolute gap vs leaders mean required to emit a finding. */
  threshold: number;
  better: BetterDirection;
  /** Which prestart-summary view supplies this metric. */
  view: 'prestart' | 'acceleration' | 'maxbsp' | 'reach' | 'leg1';
  /** Ranking column for Show me (defaults to metric). */
  rankKey?: string;
  ascending?: boolean;
  headlineLoss: string;
  headlineGain: string;
  recommendation: string;
}

/** Outcome / ranking metrics (always evaluated when data present). */
export const START_OUTCOME_RULES: StartRuleDef[] = [
  {
    id: 'leg1_dist',
    metric: 'Leg1_dist',
    label: 'Leg 1 distance',
    unit: 'm',
    threshold: 5,
    better: 'higher',
    view: 'leg1',
    ascending: false,
    headlineLoss: 'Behind at the first mark gate',
    headlineGain: 'Ahead at the first mark gate',
    recommendation:
      'Study how {leaders} cleared the line and built to mark 1 — match their gun-line speed and acceleration timing.',
  },
  {
    id: 'prestart_dist',
    metric: 'Prestart_dist',
    label: 'Distance past line at +10 s',
    unit: 'm',
    threshold: 5,
    better: 'higher',
    view: 'prestart',
    ascending: false,
    headlineLoss: 'Behind the fleet off the line',
    headlineGain: 'Clearing the line with the leaders',
    recommendation:
      'Replicate {leaders} approach: turnback timing, burn, and boat speed at the gun.',
  },
];

/** Decomposition rules — only emit when gap exceeds threshold. */
export const START_DECOMPOSITION_RULES: StartRuleDef[] = [
  {
    id: 'dtl_start',
    metric: 'DTL_start',
    label: 'Distance to line at gun',
    unit: 'm',
    threshold: 10,
    better: 'lower',
    view: 'prestart',
    ascending: true,
    headlineLoss: 'Too far from the line at the gun',
    headlineGain: 'Closer to the line at the gun than the leaders',
    recommendation:
      'Close the gap to the line earlier — leaders averaged {value} DTL at the gun ({leaders}).',
  },
  {
    id: 'bsp_start',
    metric: 'Bsp_start',
    label: 'Boat speed at gun',
    unit: 'kph',
    threshold: 3,
    better: 'higher',
    view: 'prestart',
    ascending: false,
    headlineLoss: 'Slow at the gun',
    headlineGain: 'Faster at the gun than the leaders',
    recommendation:
      'Build more speed before the gun — leaders hit {value} at the start ({leaders}).',
  },
  {
    id: 'line_perc',
    metric: 'LINE_PERC_start',
    label: 'Line position',
    unit: '%',
    threshold: 8,
    better: 'higher',
    view: 'prestart',
    ascending: false,
    headlineLoss: 'Different end of the line than the leaders',
    headlineGain: 'Lined up with the leaders on the line',
    recommendation:
      'Review line bias and preferred end — leaders started near {value} line % ({leaders}).',
  },
  {
    id: 'time_turnback',
    metric: 'Time_turnback',
    label: 'Turnback time',
    unit: 's',
    threshold: 4,
    better: 'higher',
    view: 'prestart',
    ascending: false,
    headlineLoss: 'Turnback timing differs from the leaders',
    headlineGain: 'Turnback timing matches the leaders',
    recommendation:
      'Align turnback with the pack — leaders turned at {value} relative to the gun ({leaders}).',
  },
  {
    id: 'ttk_burn',
    metric: 'TTK_burn',
    label: 'TTK burn in turnback',
    unit: 's',
    threshold: 2,
    better: 'lower',
    view: 'prestart',
    ascending: true,
    headlineLoss: 'Burning more time in the turnback',
    headlineGain: 'Burning less time in the turnback than the leaders',
    recommendation:
      'Tighten the turnback — leaders burned {value} of TTK ({leaders}).',
  },
  {
    id: 'accel_max',
    metric: 'Accel_max',
    label: 'Peak acceleration',
    unit: 'm/s²',
    threshold: 0.3,
    better: 'higher',
    view: 'acceleration',
    ascending: false,
    headlineLoss: 'Weaker peak acceleration',
    headlineGain: 'Stronger peak acceleration than the leaders',
    recommendation:
      'Match leaders\' acceleration setup — they peaked at {value} ({leaders}).',
  },
  {
    id: 'bsp_max',
    metric: 'Bsp_max',
    label: 'Max boat speed',
    unit: 'kph',
    threshold: 2,
    better: 'higher',
    view: 'maxbsp',
    ascending: false,
    headlineLoss: 'Lower top speed after the start',
    headlineGain: 'Higher top speed after the start than the leaders',
    recommendation:
      'Build toward the leaders\' peak speed of {value} ({leaders}).',
  },
  {
    id: 'reach_dist',
    metric: 'Reach_dist',
    label: 'Reach distance',
    unit: 'm',
    threshold: 5,
    better: 'higher',
    view: 'reach',
    ascending: false,
    headlineLoss: 'Behind on the reach cut',
    headlineGain: 'Ahead on the reach cut',
    recommendation:
      'Study how {leaders} carried speed through the reach — they covered {value}.',
  },
];

/** Settings at peak accel / max BSP — compare setup numbers vs leaders. */
export interface StartSettingRuleDef {
  id: string;
  metric: string;
  label: string;
  unit: string;
  threshold: number;
  view: 'acceleration' | 'maxbsp';
  recommendation: string;
}

export const START_SETTING_RULES: StartSettingRuleDef[] = [
  {
    id: 'cant_accmax',
    metric: 'Cant_accmax',
    label: 'Cant at max accel',
    unit: 'deg',
    threshold: 1.5,
    view: 'acceleration',
    recommendation: 'Try leaders\' cant at peak accel: {value} ({leaders}).',
  },
  {
    id: 'heel_accmax',
    metric: 'Heel_accmax',
    label: 'Heel at max accel',
    unit: 'deg',
    threshold: 1.5,
    view: 'acceleration',
    recommendation: 'Match leaders\' heel at peak accel: {value} ({leaders}).',
  },
  {
    id: 'rh_accmax',
    metric: 'RH_lwd_accmax',
    label: 'Ride height at max accel',
    unit: 'mm',
    threshold: 20,
    view: 'acceleration',
    recommendation: 'Match leaders\' ride height at peak accel: {value} ({leaders}).',
  },
  {
    id: 'jib_sheet_accmax',
    metric: 'Jib_sheet_load_accmax',
    label: 'Jib sheet at max accel',
    unit: 'kgf',
    threshold: 50,
    view: 'acceleration',
    recommendation: 'Match leaders\' jib sheet load at peak accel: {value} ({leaders}).',
  },
  {
    id: 'wing_twist_accmax',
    metric: 'Wing_twist_accmax',
    label: 'Wing twist at max accel',
    unit: 'deg',
    threshold: 1.5,
    view: 'acceleration',
    recommendation: 'Match leaders\' wing twist at peak accel: {value} ({leaders}).',
  },
  {
    id: 'ca1_accmax',
    metric: 'CA1_accmax',
    label: 'CA1 at max accel',
    unit: 'deg',
    threshold: 1.5,
    view: 'acceleration',
    recommendation: 'Match leaders\' CA1 at peak accel: {value} ({leaders}).',
  },
  {
    id: 'cant_bspmax',
    metric: 'Cant_bspmax',
    label: 'Cant at max BSP',
    unit: 'deg',
    threshold: 1.5,
    view: 'maxbsp',
    recommendation: 'Try leaders\' cant at max BSP: {value} ({leaders}).',
  },
  {
    id: 'heel_bspmax',
    metric: 'Heel_bspmax',
    label: 'Heel at max BSP',
    unit: 'deg',
    threshold: 1.5,
    view: 'maxbsp',
    recommendation: 'Match leaders\' heel at max BSP: {value} ({leaders}).',
  },
  {
    id: 'rh_bspmax',
    metric: 'RH_lwd_bspmax',
    label: 'Ride height at max BSP',
    unit: 'mm',
    threshold: 20,
    view: 'maxbsp',
    recommendation: 'Match leaders\' ride height at max BSP: {value} ({leaders}).',
  },
  {
    id: 'jib_sheet_bspmax',
    metric: 'Jib_sheet_load_bspmax',
    label: 'Jib sheet at max BSP',
    unit: 'kgf',
    threshold: 50,
    view: 'maxbsp',
    recommendation: 'Match leaders\' jib sheet load at max BSP: {value} ({leaders}).',
  },
  {
    id: 'wing_twist_bspmax',
    metric: 'Wing_twist_bspmax',
    label: 'Wing twist at max BSP',
    unit: 'deg',
    threshold: 1.5,
    view: 'maxbsp',
    recommendation: 'Match leaders\' wing twist at max BSP: {value} ({leaders}).',
  },
  {
    id: 'ca1_bspmax',
    metric: 'CA1_bspmax',
    label: 'CA1 at max BSP',
    unit: 'deg',
    threshold: 1.5,
    view: 'maxbsp',
    recommendation: 'Match leaders\' CA1 at max BSP: {value} ({leaders}).',
  },
];
